import { Typography } from "@mui/material";
import { BASE_URL } from "../../../constants/apiConstants";
import { useTranslation } from "react-i18next";

// The backend sometimes returns a server-relative path ("/media/xxx.jpg") and
// sometimes a full absolute URL (e.g. activities added from the phone via the
// remote dev server come back as "https://lmsdev.sabeqoon.com/media/xxx.jpg").
// Blindly prepending BASE_URL breaks the absolute case ("https://…comhttps://…").
// This mirrors the `startsWith('http') ? url : BASE_URL + url` guard used
// throughout the Flutter app (activity_card.dart, activity_type_screen.dart, etc.)
const resolveMediaUrl = (path) => {
  if (!path) return path;
  // Safety net: some list fields (see FeedComponent's extractImageUrl) can
  // still hand us an object like { image | url | file } if a new endpoint
  // returns that shape before normalizeActivity accounts for it.
  if (typeof path !== "string") {
    path = path.image ?? path.url ?? path.file ?? null;
    if (!path) return null;
  }
  return path.startsWith("http") ? path : `${BASE_URL}${path}`;
};

const RenderImage = ({ src, alt }) => (
  <img
    src={resolveMediaUrl(src)}
    alt={alt}
    style={{
      width: 90,
      height: 90,
      borderRadius: 4,
      objectFit: "cover",
      marginBottom: 8,
    }}
  />
);

// Shared by every activity type that carries the generic `images` array
// (media, custom, meds, incident, note, achievement all confirmed to use
// this same shape in the real feed payload).
const RenderImages = ({ images }) =>
  images?.length > 0 ? (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
      {images.map((img, i) => (
        <RenderImage key={i} src={img} alt={`Activity visual ${i + 1}`} />
      ))}
    </div>
  ) : null;

const RenderVideo = ({ video }) =>
  video ? (
    <video controls style={{ width: 200, borderRadius: 4 }}>
      <source src={resolveMediaUrl(video)} type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  ) : null;

const RenderNote = ({ note, label = "note" }) => {
  const { t } = useTranslation();
  const defaults = { note: "Note", noteRevision: "Revision note" };
  return note ? (
    <Typography variant="body2" color="text.secondary" className="mt-2">
      <strong>
        {t(`studentDetails.activityCard.${label}`, { defaultValue: defaults[label] ?? label })}:
      </strong>{" "}
      {note}
    </Typography>
  ) : null;
};

const LabeledText = ({ label, value }) =>
  value || value === 0 ? (
    <Typography variant="body2" color="text.secondary">
      <strong>{label}:</strong> {value}
    </Typography>
  ) : null;

// Generic attachment for skill/observation/subjectshomework/custom activities.
// `file` is a server-relative path or absolute URL (see resolveMediaUrl).
const RenderFile = ({ file }) => {
  const { t } = useTranslation();
  return file ? (
    <Typography variant="body2" className="mt-2">
      <a href={resolveMediaUrl(file)} target="_blank" rel="noopener noreferrer">
        {t("studentDetails.activityCard.attachment", { defaultValue: "Attachment" })}
      </a>
    </Typography>
  ) : null;
};

export const ActivityCard = ({ type, details }) => {
  const { t } = useTranslation();
  switch (type) {
    case "meds":
    case "incident":
    case "note":
    case "achievement":
    case "kudos":
      // Confirmed against a real payload: these all carry a generic
      // `images` array (not the old single MedsImage/IncidentImage/etc.
      // field). "meds" hasn't been seen in a real payload yet but is kept
      // on the same shape since every other simple type follows this
      // pattern now.
      return (
        <>
          <RenderImages images={details.ActivityImages} />
          <RenderNote note={details.Note} />
        </>
      );

    case "photo":
    case "media":
    case "custom":
      // media and custom share the same confirmed shape: images, an
      // optional video, a note — custom additionally has an optional file
      // attachment and tags (custom_types), rendered as the card title.
      return (
        <div style={{ display: "grid", gap: "12px", justifyContent: "start" }}>
          <RenderImages images={details.ActivityImages} />
          <RenderVideo video={details.ActivityVideo} />
          {type === "custom" && <RenderFile file={details.File} />}
          <RenderNote note={details.Note} />
        </div>
      );

    case "assessment":
    case "quranassessment":
      // Confirmed against a real payload: this is a single flat record, not
      // a list of audio_files entries — one audio clip plus juz/sura/aya
      // range and scores directly on the activity.
      return (
        <>
          {details.Audio && (
            <audio controls style={{ borderRadius: 4 }}>
              <source src={resolveMediaUrl(details.Audio)} type="audio/mp4" />
              Your browser does not support the audio element.
            </audio>
          )}
          {/* Juz/Sura are numeric IDs — the feed doesn't include their names,
              so these show as numbers until a juz/sura name lookup (like the
              custom_types cache) is added. */}
          {(details.JuzFrom || details.JuzTo) && (
            <LabeledText
              label={t("studentDetails.activityCard.juz", { defaultValue: "Juz" })}
              value={`${details.JuzFrom ?? ""} ${t("studentDetails.activityCard.to", { defaultValue: "to" })} ${details.JuzTo ?? ""}`}
            />
          )}
          {(details.SuraFrom || details.SuraTo) && (
            <LabeledText
              label={t("studentDetails.activityCard.sura", { defaultValue: "Sura" })}
              value={`${details.SuraFrom ?? ""} ${t("studentDetails.activityCard.to", { defaultValue: "to" })} ${details.SuraTo ?? ""}`}
            />
          )}
          {(details.AyaFrom || details.AyaTo) && (
            <LabeledText
              label={t("studentDetails.activityCard.newAya", { defaultValue: "Aya" })}
              value={`${details.AyaFrom ?? ""} ${t("studentDetails.activityCard.to", { defaultValue: "to" })} ${details.AyaTo ?? ""}`}
            />
          )}
          {details.MistakesTelawa !== undefined && details.MistakesTelawa !== 0 && (
            <LabeledText
              label={t("studentDetails.activityCard.telawaMistakes", { defaultValue: "Telawa mistakes" })}
              value={details.MistakesTelawa}
            />
          )}
          {details.MistakesTagweed !== undefined && details.MistakesTagweed !== 0 && (
            <LabeledText
              label={t("studentDetails.activityCard.tagweedMistakes", { defaultValue: "Tagweed mistakes" })}
              value={details.MistakesTagweed}
            />
          )}
          {(details.Score || details.Score === 0) && (
            <LabeledText
              label={t("studentDetails.activityCard.score", { defaultValue: "Score" })}
              value={details.Score}
            />
          )}
          <RenderNote note={details.Note} />
        </>
      );

    case "skill":
      return (
        <>
          {details.SkillName && (
            <LabeledText
              label={t("studentDetails.activityCard.skill", { defaultValue: "Skill" })}
              value={details.SkillName}
            />
          )}
          {/* Progress is a plain integer (e.g. a 1-5 or 1-10 rating), not a % or free text */}
          {(details.Progress || details.Progress === 0) && (
            <LabeledText
              label={t("studentDetails.activityCard.progress", { defaultValue: "Progress" })}
              value={details.Progress}
            />
          )}
          <RenderFile file={details.File} />
          <RenderNote note={details.Note} />
        </>
      );

    case "observation":
      return (
        <>
          <RenderFile file={details.File} />
          <RenderNote note={details.Note} />
        </>
      );

    case "subjectshomework":
      return (
        <>
          {details.DueDate && (
            <LabeledText
              label={t("studentDetails.activityCard.dueDate", { defaultValue: "Due date" })}
              value={details.DueDate}
            />
          )}
          <RenderFile file={details.File} />
          <RenderNote note={details.Note} />
        </>
      );

    case "checkin":
      return (
        <>
          <Typography
            variant="body1"
            fontWeight="bold"
            color={
              details.attendance_status?.toLowerCase() === "absent"
                ? "error.main"
                : "success.main"
            }
          >
            {t("studentDetails.activityCard.status", { defaultValue: "Status" })}:{" "}
            {details.attendance_status}
          </Typography>
          <RenderNote note={details.Note} />
        </>
      );

    case "homework":
    case "quran":
    case "recitation": {
      // Confirmed against a real payload: new_sura/revision_sura/new_juz/
      // revision_juz are flat numeric IDs (no nested {Name} object like the
      // old V1 shape assumed) — juz/sura names aren't in the feed response,
      // same caveat as the Quran Assessment card above.
      const juzSuraLabel = (juz, sura) =>
        juz || sura
          ? [
              juz ? `${t("studentDetails.activityCard.juz", { defaultValue: "Juz" })} ${juz}` : null,
              sura ? `${t("studentDetails.activityCard.sura", { defaultValue: "Sura" })} ${sura}` : null,
            ]
              .filter(Boolean)
              .join(", ")
          : null;

      return (
        <>
          {(details.NewAyaFrom || details.NewAyaTo) && (
            <LabeledText
              label={t("studentDetails.activityCard.newAya", { defaultValue: "New" })}
              value={`${juzSuraLabel(details.NewJuz, details.NewSura) ?? ""} (${details.NewAyaFrom ?? ""} ${t(
                "studentDetails.activityCard.to",
                { defaultValue: "to" }
              )} ${details.NewAyaTo ?? ""})`}
            />
          )}
          {details.NewJuzMul?.length > 0 && (
            <LabeledText
              label={t("studentDetails.activityCard.juz", { defaultValue: "Juz" })}
              value={details.NewJuzMul.join(", ")}
            />
          )}
          {details.NewSuraMul?.length > 0 && (
            <LabeledText
              label={t("studentDetails.activityCard.sura", { defaultValue: "Sura" })}
              value={details.NewSuraMul.join(", ")}
            />
          )}
          {(details.RevisionAyaFrom || details.RevisionAyaTo) && (
            <LabeledText
              label={t("studentDetails.activityCard.ayahs", { defaultValue: "Revision" })}
              value={`${juzSuraLabel(details.RevisionJuz, details.RevisionSura) ?? ""} (${details.RevisionAyaFrom ?? ""} ${t(
                "studentDetails.activityCard.to",
                { defaultValue: "to" }
              )} ${details.RevisionAyaTo ?? ""})`}
            />
          )}
          {details.RevisionJuzMul?.length > 0 && (
            <LabeledText
              label={t("studentDetails.activityCard.revisionSura", { defaultValue: "Revision Juz" })}
              value={details.RevisionJuzMul.join(", ")}
            />
          )}
          {details.RevisionSuraMul?.length > 0 && (
            <LabeledText
              label={t("studentDetails.activityCard.revisionSura", { defaultValue: "Revision Sura" })}
              value={details.RevisionSuraMul.join(", ")}
            />
          )}
          {type === "recitation" && (
            <>
              {!!details.MistakesTelawaMemorization && (
                <LabeledText
                  label={t("studentDetails.activityCard.telawaMistakes", { defaultValue: "Telawa mistakes" })}
                  value={details.MistakesTelawaMemorization}
                />
              )}
              {!!details.MistakesTagweedMemorization && (
                <LabeledText
                  label={t("studentDetails.activityCard.tagweedMistakes", { defaultValue: "Tagweed mistakes" })}
                  value={details.MistakesTagweedMemorization}
                />
              )}
              {(details.ScoreMemorization || details.ScoreMemorization === 0) && (
                <LabeledText
                  label={t("studentDetails.activityCard.memorizationScore", { defaultValue: "Memorization score" })}
                  value={details.ScoreMemorization}
                />
              )}
              {!!details.MistakesTelawaRevision && (
                <LabeledText
                  label={t("studentDetails.activityCard.telawaMistakes", { defaultValue: "Telawa mistakes (revision)" })}
                  value={details.MistakesTelawaRevision}
                />
              )}
              {!!details.MistakesTagweedRevision && (
                <LabeledText
                  label={t("studentDetails.activityCard.tagweedMistakes", { defaultValue: "Tagweed mistakes (revision)" })}
                  value={details.MistakesTagweedRevision}
                />
              )}
              {(details.ScoreRevision || details.ScoreRevision === 0) && (
                <LabeledText
                  label={t("studentDetails.activityCard.recitationScore", { defaultValue: "Revision score" })}
                  value={details.ScoreRevision}
                />
              )}
            </>
          )}
          <RenderNote note={details.RevisionNote} label="noteRevision" />
          <RenderNote note={details.Note} />
        </>
      );
    }

    default:
      // Unknown/not-yet-mapped activity_type from the backend: at least
      // render the note if one is present instead of showing nothing.
      return details?.Note || details?.note ? (
        <RenderNote note={details.Note || details.note} />
      ) : null;
  }
};